import store from 'store2'
import React, { useContext, useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { SipContext } from '~/service/SipConn'
import { CampaignManagerContext } from '~/service/CampaignManager'
import { VolumeMeterContext } from '~/service/VolumeMeter'
import { WebsocketContext } from '~/service/WebsocketConn'
import { getToken } from '~/utils/token'
import { IframeContext } from '~/service/IframeConn'
import { FormattedMessage } from '~/translations/utils'
import { PauseContext } from '~/service/Pause'
import { NavLink, useHistory } from 'react-router-dom'
import { NotificationsPromptButton } from './NotificationsPrompt'
import Tooltip from './common/Tooltip'
import useResizeObserver from 'use-resize-observer'
import { ClientListManagerContext } from '~/service/ClientListManager'

const logout = () => {
  store.remove('token')
  window.location.href = ''
}

const Header: React.FC = ({}) => {
  const { parentRef } = useContext(IframeContext)
  const ref = useRef<HTMLDivElement>(null)

  const close = () => parentRef.current?.close()

  return (
    <div className="header" ref={ref}>
      {window.c.phoneEnabled && (
        <>
          <StatusSip />
          <ActivateTab />
        </>
      )}
      <StatusPhoenix />
      <Campaign />
      <Drag />

      <div className="header-buttons">
        {window.c.phoneEnabled && (
          <Tooltip content={intl.formatMessage({ id: 'header / settings' })}>
            <NavLink to="/settings" activeClassName="is-active">
              <span className="far fa-cog" />
            </NavLink>
          </Tooltip>
        )}

        <NotificationsPromptButton />

        <Pause />

        {!window.isIframe && (
          <Tooltip content={intl.formatMessage({ id: 'header / logout' })}>
            <button onClick={logout}>
              <span className="far fa-sign-out" />
            </button>
          </Tooltip>
        )}

        {window.isIframe && (
          <>
            <a href={'/?token=' + getToken()} target="_blank">
              <span className="far fa-external-link-alt" />
            </a>
            <button onClick={close}>
              <span className="far fa-chevron-down" />
            </button>
          </>
        )}
      </div>
    </div>
  )
}

export default Header

const Campaign: React.FC = ({}) => {
  const {
    activeCampaign: campaign,
    loading,
    next,
    stop,
  } = useContext(CampaignManagerContext)
  const { activeCall } = useContext(SipContext)
  const history = useHistory()

  const { setFilter } = useContext(ClientListManagerContext)

  if (!campaign) {
    return null
  }

  const openList = () => {
    setFilter(filter => ({ ...filter, tmCampaign: campaign.id }))

    history.push('/clients')
  }

  return (
    <div className="header-campaign">
      <div className="header-campaign-buttons">
        <Tooltip
          content={intl.formatMessage({ id: 'header / tm campaign / stop' })}
        >
          <button disabled={loading} onClick={stop}>
            <span className="fa fa-stop" />
          </button>
        </Tooltip>

        <Tooltip
          content={intl.formatMessage({
            id: 'header / tm campaign / next client',
          })}
        >
          <button onClick={next} disabled={loading || !!activeCall}>
            <span className="fa fa-step-forward" />
          </button>
        </Tooltip>

        <Tooltip
          content={intl.formatMessage({
            id: 'header / tm campaign / client list',
          })}
        >
          <button disabled={loading || !!activeCall} onClick={openList}>
            <span className="fa fa-list" />
          </button>
        </Tooltip>
      </div>
      <div className="header-campaign-title">{campaign.name}</div>
    </div>
  )
}

const StatusPhoenix: React.FC = ({}) => {
  const wsContext = useContext(WebsocketContext)

  if (wsContext.status == 'success') {
    return null
  }

  const label =
    wsContext.status == 'loading'
      ? intl.formatMessage({
          id: 'header / phoenix status / tooltip / loading',
        })
      : intl.formatMessage({
          id: 'header / phoenix status / tooltip / fail',
        })

  return (
    <Tooltip content={label}>
      <div className={'header-status is-' + wsContext.status}>
        {wsContext.status == 'fail' && (
          <span className="fa fa-exclamation-circle" />
        )}
      </div>
    </Tooltip>
  )
}

const ActivateTab: React.FC = ({}) => {
  const { userChannel } = useContext(WebsocketContext)
  const sipContext = useContext(SipContext)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (sipContext.registrationStatus == 'disabled') {
      setLoading(false)
    }
  }, [sipContext.registrationStatus])

  if (sipContext.registrationStatus != 'disabled') {
    return null
  }

  return (
    <Tooltip
      content={intl.formatMessage({ id: 'header / activate tab / tooltip' })}
    >
      <button
        className="header-activateTab"
        disabled={loading}
        onClick={() => {
          setLoading(true)
          userChannel?.push('activate_tab', {})
        }}
      >
        {loading ? (
          <span className="fa fa-spin fa-spinner-third" />
        ) : (
          <span className="fa fa-signal-alt-slash" />
        )}
        <div className="header-activateTab-label">
          <FormattedMessage id="header / activate tab / button" autop={true} />
        </div>
      </button>
    </Tooltip>
  )
}

const StatusSip: React.FC = ({}) => {
  const sipContext = useContext(SipContext)
  const { localRef } = useContext(VolumeMeterContext)

  if (sipContext.registrationStatus == 'disabled') {
    return null
  }

  if (sipContext.userAgentConnectionStatus == 'disconnected') {
    return (
      <Tooltip
        content={intl.formatMessage({
          id: 'header / status / sip / disconnected',
        })}
      >
        <div className="header-status is-disconnected">
          <div />
        </div>
      </Tooltip>
    )
  }

  const [label, icon] = (() => {
    switch (sipContext.registrationStatus) {
      case 'loading':
        return [
          intl.formatMessage({ id: 'header / status / sip / loading' }),
          <span className="fa fa-spin fa-spinner-third" />,
        ]

      case 'success':
        return [
          intl.formatMessage({ id: 'header / status / sip / success' }),
          <div className="header-status-dot" />,
        ]

      case 'fail':
        return [
          intl.formatMessage({ id: 'header / status / sip / fail' }),
          <span className="fa fa-exclamation-circle" />,
        ]
    }
  })()

  return (
    <Tooltip content={label}>
      <div className={'header-status is-' + sipContext.registrationStatus}>
        {icon}

        {sipContext.registrationStatus == 'success' && (
          <div className="header-status-volume" ref={localRef} />
        )}
      </div>
    </Tooltip>
  )
}

const Pause: React.FC = ({}) => {
  const {
    activePhone,
    activeChat,
    isLoadingChat,
    isLoadingPhone,
    pauseEnable,
    pauseDisable,
    activeChatSupervisor,
  } = useContext(PauseContext)

  const isLoading = isLoadingChat || isLoadingPhone

  const activeEnabledByUser = activePhone || activeChat
  const active = activeEnabledByUser || activeChatSupervisor

  return (
    <div className="header-pause">
      <Tooltip
        placement={
          window.c.chatEnabled && window.c.phoneEnabled ? 'left' : undefined
        }
        content={
          window.c.chatEnabled && window.c.phoneEnabled
            ? active
              ? intl.formatMessage({ id: 'header / pause / main / disable' })
              : intl.formatMessage({ id: 'header / pause / main / enable' })
            : active
            ? intl.formatMessage({ id: 'header / pause / chat / disable' })
            : intl.formatMessage({ id: 'header / pause / chat / enable' })
        }
      >
        <button
          className={classNames('header-pause-main', {
            'is-active': active,
            'is-active-supervisor': activeChatSupervisor,
          })}
          disabled={isLoading}
          onClick={() => {
            if (activeEnabledByUser) {
              pauseDisable('phone')
              pauseDisable('chat')
            } else {
              pauseEnable('phone')
              pauseEnable('chat')
            }
          }}
        >
          {isLoading ? (
            <span className="fa fa-spinner-third fa-spin" />
          ) : (
            <>
              {active && (
                <div className="header-pause-main-steam">
                  <div />
                  <div />
                  <div />
                  <div />
                </div>
              )}

              <span className={active ? 'fa fa-coffee' : 'far fa-coffee'} />
            </>
          )}
        </button>
      </Tooltip>

      {window.c.chatEnabled && window.c.phoneEnabled && (
        <>
          <Tooltip
            placement={'bottom'}
            content={
              activePhone
                ? intl.formatMessage({ id: 'header / pause / phone / disable' })
                : intl.formatMessage({ id: 'header / pause / phone / enable' })
            }
          >
            <button
              className={classNames('header-pause-sub', {
                'is-active': activePhone,
              })}
              disabled={isLoading}
              onClick={() =>
                activePhone ? pauseDisable('phone') : pauseEnable('phone')
              }
            >
              <span
                className={
                  isLoadingPhone
                    ? 'fa fa-spinner-third fa-spin'
                    : activePhone
                    ? 'fa fa-phone'
                    : 'far fa-phone'
                }
              />
            </button>
          </Tooltip>

          <Tooltip
            placement={'bottom'}
            content={
              activeChatSupervisor
                ? intl.formatMessage({
                    id: 'header / pause / chat / supervisor',
                  })
                : activeChat
                ? intl.formatMessage({ id: 'header / pause / chat / disable' })
                : intl.formatMessage({ id: 'header / pause / chat / enable' })
            }
          >
            <button
              className={classNames('header-pause-sub', {
                'is-active': activeChat || activeChatSupervisor,
                'is-active-supervisor': activeChatSupervisor,
              })}
              disabled={isLoading}
              onClick={() => {
                activeChat ? pauseDisable('chat') : pauseEnable('chat')
              }}
            >
              <span
                className={
                  isLoadingChat
                    ? 'fa fa-spinner-third fa-spin'
                    : activeChat
                    ? 'fa fa-comment-alt'
                    : 'far fa-comment-alt'
                }
              />
            </button>
          </Tooltip>
        </>
      )}
    </div>
  )
}

const Drag: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null)
  const { width = 1 } = useResizeObserver<HTMLDivElement>({ ref })
  const { parentRef } = useContext(IframeContext)

  useEffect(() => {
    const rect = ref.current?.getBoundingClientRect()

    if (rect) {
      parentRef.current?.setDragHandlerRect(rect)
    }
  }, [width])

  return <div className="header-drag" ref={ref}></div>
}
